import React, { useState, useMemo } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, Progress, Note, Badge, AwardBadge, AwardTrophy, PilulaAward, PilulaAwardTime, Btn } from "./ui";
import { Icon } from "./Icons";
import { AWARDS, METAS_REDE, TIMES } from "./data";
import {
  calcPontosCorretor, calcAwardAtual, calcAwardVitalicio, awardPorPontos,
  calcProximoAward, calcElegibilidade, rankingGeral,
  calcPorTime, calcPorRegional, calcNacional, calcAceleradoresCorretor,
} from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// PÁGINA RANKING & AWARDS
// Mostra: perfil do corretor logado (ou visão geral se admin) + ranking
// ═══════════════════════════════════════════════════════════════════════

export default function Ranking({ corretores, currentUser }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Ranking Geral"
        subtitle="Sua jornada na Bellanor Alta Performance"
        icon={<Icon.trophy size={24} />}
      />
      <RankingGeral corretores={corretores} currentUser={currentUser} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PERFIL DO CORRETOR — A tela mais importante do app
// ═══════════════════════════════════════════════════════════════════════

function PerfilCorretor({ corretor: c, corretores, metas, aceleradores, premios }) {
  const pts = calcPontosCorretor(c);
  const awardAtual = awardPorPontos(pts.total);
  const awardVit = calcAwardVitalicio(c);
  const prox = calcProximoAward(pts.total);
  const elegibilidade = calcElegibilidade(c);
  const time = TIMES.find(t => t.id === c.timeId);

  const timesAgg = useMemo(() => calcPorTime(corretores), [corretores]);
  const regs = useMemo(() => calcPorRegional(corretores), [corretores]);
  const nacional = useMemo(() => calcNacional(corretores), [corretores]);
  const accel = useMemo(() =>
    calcAceleradoresCorretor({ corretor: c, times: timesAgg, regionais: regs, nacional, metas, aceleradores }),
    [c, timesAgg, regs, nacional, metas, aceleradores]
  );

  const awardColor = AWARD_COLORS[awardAtual.cor];
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ═══ HERO — Identidade + Award atual (com troféu físico) ═══ */}
      <div style={{
        background: `linear-gradient(135deg, ${awardColor.bg} 0%, ${T.card} 60%, ${T.bg} 100%)`,
        border: `1px solid ${awardColor.border}`,
        borderRadius: T.r.xl, padding: "32px 28px",
        position: "relative", overflow: "hidden",
      }}>
        <div className="hero-profile" style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 24, alignItems: "center", position: "relative" }}>

          {/* Troféu à esquerda — voando, sem label duplicada */}
          <div className="hero-badge" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 140 }}>
            <AwardTrophy awardId={awardAtual.id} size={150} />
          </div>

          {/* Identidade no centro */}
          <div className="hero-identity" style={{ minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 10 }}>
              <Avatar initials={initials} size={80} tone={T.brand} photo={c.foto} />
              <div>
                <div className="serif" style={{
                  fontSize: 26, fontWeight: 600, color: T.brand,
                  letterSpacing: -0.5, lineHeight: 1.05,
                }}>
                  {c.nome}
                </div>
                {time && (
                  <div style={{ fontSize: 11, color: T.textSub, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 13, lineHeight: 1 }}>{time.emoji || "🏆"}</span> {time.nome}
                  </div>
                )}
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon.pin size={11} /> {c.cidade}-{c.uf}
                </div>
              </div>
            </div>

            {awardVit.id !== awardAtual.id && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: AWARD_COLORS[awardVit.cor].bg,
                border: `1px solid ${AWARD_COLORS[awardVit.cor].border}`,
                padding: "6px 12px", borderRadius: 20,
                fontSize: 11, color: AWARD_COLORS[awardVit.cor].fg, fontWeight: 600,
              }}>
                <Icon.crown size={12} />
                Status vitalício: <b>{awardVit.nome}</b>
              </div>
            )}
          </div>

          {/* Pontos à direita (ou "Ranking Score" se Diamond) */}
          <div className="hero-points" style={{ textAlign: "right", minWidth: 130 }}>
            <div style={{ fontSize: 9, color: T.textSub, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 }}>
              {awardAtual.id === "diamond" ? `Ranking Score ${new Date().getFullYear()}` : `Pontos ${new Date().getFullYear()}`}
            </div>
            <div className="serif" style={{
              fontSize: 68, fontWeight: 700, color: awardColor.fg,
              lineHeight: 0.9, letterSpacing: -3, marginTop: 2,
            }}>
              {pts.total}
            </div>
            {accel.total > 0 && (
              <div style={{
                fontSize: 11, color: T.ok, fontWeight: 600, marginTop: 6,
                display: "inline-flex", alignItems: "center", gap: 4,
                background: T.okSoft, padding: "3px 8px", borderRadius: 10,
                border: `1px solid ${T.ok}33`,
              }}>
                <Icon.rocket size={11} /> +{accel.pontosBonus} pts de bônus
              </div>
            )}
          </div>

          {/* Progresso para próximo award (dentro do grid pra reordenar no mobile) */}
          <div className="hero-progress" style={{ gridColumn: "1 / -1" }}>
            {prox && (
              <div style={{ marginTop: 10, padding: 14, background: T.bg + "cc", borderRadius: T.r.md, border: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textSub, marginBottom: 8, alignItems: "center" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span>Próximo nível:</span>
                    <b style={{ color: AWARD_COLORS[prox.prox.cor].fg, fontSize: 13 }}>{prox.prox.nome}</b>
                  </span>
                  <span>Faltam <b style={{ color: T.brand, fontSize: 13 }}>{prox.faltam}</b> pts</span>
                </div>
                <Progress value={pts.total} max={prox.prox.pts} color={awardColor.fg} height={8} />
              </div>
            )}
            {!prox && (
              <div style={{ marginTop: 8 }}>
                <Note tone="brand" icon={<Icon.crown size={14} />}>
                  Você alcançou o nível máximo — <b>Diamond Award</b>. Continue acumulando pontos para elegibilidade ao BYD e ao programa de franquias.
                </Note>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ DETALHAMENTO DE PONTOS ═══ */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 16 }}>
          Detalhamento da Pontuação
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <BlocoPontos
            icon={<Icon.handshake size={18} />}
            label="Vendas diretas"
            valor={pts.pVendas}
            detalhe={`${c.vendasDiretas} vendas · ${fmt.brl(c.vendasValor)}`}
            color={T.brand}
            info="1 venda paga = 1 ponto"
          />
          <BlocoPontos
            icon={<Icon.users size={18} />}
            label="Ativações da rede"
            valor={pts.pAtivados}
            detalhe={`${c.ativados} ativados de ${c.cadastrados} cadastrados`}
            color={T.info}
            info="1 corretor ativado = 10 pontos"
          />
        </div>

        {accel.total > 0 && (
          <div style={{ marginTop: 16, padding: 14, background: T.okSoft, borderRadius: T.r.md, border: `1px solid ${T.ok}30` }}>
            <div style={{ fontSize: 11, color: T.ok, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon.rocket size={12} /> Aceleradores ativos · +{accel.total} pts
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {accel.detalhes.map((d, i) => (
                <div key={i} style={{ fontSize: 12, color: T.textSec, display: "flex", justifyContent: "space-between" }}>
                  <span>{d.label}</span>
                  <b style={{ color: T.ok }}>+{d.pct} pts</b>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 8, fontStyle: "italic" }}>
              Aceleradores são cumulativos e somados aos {pts.total} pontos base, gerando +{accel.pontosBonus} pts de bônus.
            </div>
          </div>
        )}
      </Card>

      {/* ═══ RECOMPENSAS DESBLOQUEADAS ═══ */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 16 }}>
          Recompensas do seu nível {awardVit.id !== "cafe" ? `(${awardVit.nome})` : ""}
        </div>

        {awardVit.id === "cafe" ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: T.textDim, fontSize: 13 }}>
            Você ainda não conquistou nenhum award. Realize suas primeiras 50 vendas ou ative 5 corretores em sua rede para desbloquear o Bronze Award.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Recompensa icon="✓" texto={`Comissão adicional de +${awardVit.comissaoAdd}% sobre ${awardVit.comissaoEscopo}`} ok />
            <Recompensa icon="✓" texto={`Rede de relacionamento liberada em ${awardVit.niveisRede} níveis`} ok />
            <Recompensa icon="✓" texto={`Badge ${awardVit.nome} no ranking`} ok />
            {awardVit.quadro && <Recompensa icon="✓" texto={`Quadro decorativo troféu ${awardVit.nome}`} ok />}
            {awardVit.franquia && (
              <Recompensa
                icon="◆"
                texto="Elegível ao Programa de Franquias Bellanor"
                ok
                ressalva="Participação opcional, sujeita a análise e aporte compartilhado — disponibilidade regional a critério da Bellanor."
              />
            )}
          </div>
        )}
      </Card>

      {/* ═══ ELEGIBILIDADE AOS PRÊMIOS GRANDES ═══ */}
      <Card>
        <div style={{
          fontSize: 11, color: T.textSub, textTransform: "uppercase",
          letterSpacing: 1, fontWeight: 600, marginBottom: 6,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Icon.gift size={12} /> Prêmios Anuais {new Date().getFullYear()}
        </div>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 18, lineHeight: 1.6 }}>
          Prêmios <b>acumulativos</b> por volume de vendas diretas no ano. Quem atingir R$ 5M conquista os 3 ao mesmo tempo. Vendas diretas pagas no ciclo 2026 zeram em 1º de janeiro de 2027.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {elegibilidade.map(e => {
            const premioInfo = premios?.[e.award === "gold" ? "iphone" : e.award === "platinum" ? "cruzeiro" : "byd"];
            return (
              <PremioRow key={e.award} elegibilidade={e} premio={premioInfo} />
            );
          })}
        </div>
      </Card>

      {/* ═══ METAS DE EXPANSÃO DE REDE ═══ */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 16 }}>
          Metas de Expansão de Rede
        </div>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 16, lineHeight: 1.6 }}>
          Sugestão de atingimento coerente com cada nível de award (opcional — não é requisito para subir).
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          {METAS_REDE.map(m => {
            const award = AWARDS.find(a => a.id === m.award);
            const color = AWARD_COLORS[award.cor];
            const atingido = c.cadastrados >= m.novos;
            return (
              <div key={m.award} style={{
                background: atingido ? color.bg : T.input,
                border: `1px solid ${atingido ? color.border : T.border}`,
                borderRadius: T.r.md, padding: 12,
                opacity: atingido ? 1 : 0.6,
              }}>
                <div style={{ fontSize: 10, color: atingido ? color.fg : T.textDim, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                  {award.nome}
                </div>
                <div className="serif" style={{ fontSize: 22, color: atingido ? color.fg : T.textDim, fontWeight: 600, letterSpacing: -0.5, lineHeight: 1 }}>
                  {m.novos}
                </div>
                <div style={{ fontSize: 10, color: T.textSub, marginTop: 4 }}>
                  novos corretores
                </div>
                {atingido && <div style={{ fontSize: 10, color: T.ok, marginTop: 4, fontWeight: 600 }}>✓ atingido</div>}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── Bloco de pontos (venda direta / rede) ───────────────────────────
function BlocoPontos({ icon, label, valor, detalhe, color, info }) {
  return (
    <div style={{
      background: T.input, border: `1px solid ${T.border}`,
      borderRadius: T.r.md, padding: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color }}>
        {icon}
        <span style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{label}</span>
      </div>
      <div className="serif" style={{ fontSize: 32, fontWeight: 600, color: T.text, letterSpacing: -0.5, lineHeight: 1 }}>
        {valor} <span style={{ fontSize: 14, color: T.textSub }}>pts</span>
      </div>
      <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>{detalhe}</div>
      <div style={{ fontSize: 10, color: T.textDim, marginTop: 4, fontStyle: "italic" }}>{info}</div>
    </div>
  );
}

// ─── Item de recompensa ──────────────────────────────────────────────
function Recompensa({ icon, texto, ok, ressalva }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{
        width: 20, height: 20, borderRadius: 10,
        background: ok ? T.ok : T.textDim, color: T.textInv,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{texto}</div>
        {ressalva && (
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 4, fontStyle: "italic", lineHeight: 1.5 }}>
            ⓘ {ressalva}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Linha de prêmio grande ──────────────────────────────────────────
function PremioRow({ elegibilidade: e, premio }) {
  const bg = e.elegivel ? T.okSoft : T.cardSoft;
  const border = e.elegivel ? T.ok + "44" : T.border;

  return (
    <div style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: T.r.md, padding: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
            {premio?.nome || e.nomePremio}
            {e.elegivel && <Badge text="ELEGÍVEL" tone="ok" />}
          </div>
          {premio?.descricao && (
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>{premio.descricao}</div>
          )}
        </div>
        <AwardBadge awardId={e.award} size="sm" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Req
          label="Award (pontos)"
          req={`${e.ptsMin} pts`}
          atingido={e.atingiuPts}
          faltam={e.faltamPts > 0 ? `${e.faltamPts} pts` : null}
        />
        <Req
          label="Volume em vendas diretas"
          req={fmt.brl(e.volMin)}
          atingido={e.atingiuVol}
          faltam={e.faltamVol > 0 ? fmt.brl(e.faltamVol) : null}
        />
      </div>
    </div>
  );
}

function Req({ label, req, atingido, faltam }) {
  return (
    <div style={{
      background: T.input, borderRadius: 4,
      padding: "8px 10px", fontSize: 11, display: "flex", flexDirection: "column", gap: 2,
    }}>
      <div style={{ color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 9, fontWeight: 600 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: atingido ? T.ok : T.textDim, fontWeight: 700, fontSize: 14 }}>
          {atingido ? "✓" : "○"}
        </span>
        <span style={{ color: atingido ? T.ok : T.text, fontWeight: 500 }}>
          {atingido ? "Atingido" : req}
        </span>
      </div>
      {!atingido && faltam && (
        <div style={{ color: T.textDim, fontSize: 10 }}>faltam {faltam}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// RANKING GERAL — tabela com todos os corretores
// ═══════════════════════════════════════════════════════════════════════

function RankingGeral({ corretores, currentUser }) {
  const ranking = useMemo(() => rankingGeral(corretores), [corretores]);
  const top3 = ranking.slice(0, 3);
  const restante = ranking.slice(3);

  // Id do corretor logado (para destacar "você aqui")
  const meuId = currentUser?.role === "corretor" ? currentUser.corretorId : null;
  const meuUuid = currentUser?._corretorUuid || null;
  const minhaPosicao = meuId ? ranking.findIndex(c => c._uuid === meuUuid || c.id === meuId) + 1 : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Pódio 3D */}
      {top3.length >= 3 && (
        <div style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: T.r.xl,
          padding: "40px 28px 28px",
          position: "relative",
          overflow: "hidden",
        }}>
          <div className="podium-grid" style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "1fr 1.2fr 1fr",
            gap: 16,
            alignItems: "end",
            maxWidth: 720, margin: "0 auto",
          }}>
            {/* ordem visual: 2º — 1º — 3º */}
            {[top3[1], top3[0], top3[2]].map((c, i) => {
              const posReal = i === 0 ? 2 : i === 1 ? 1 : 3;
              const time = TIMES.find(t => t.id === c.timeId);
              return (
                <PodiumColumn key={c.id} corretor={c} posicao={posReal} time={time} />
              );
            })}
          </div>

          <div style={{
            textAlign: "center", fontSize: 11, color: T.textSub,
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}`,
            letterSpacing: 0.5,
          }}>
            🏆 <span className="serif" style={{ color: T.brand, fontWeight: 600, fontSize: 13 }}>Top 3</span> · atualizado em tempo real
          </div>
        </div>
      )}

      {/* Restante */}
      {restante.length > 0 && (
        <Card>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginBottom: 14 }}>
            Classificação Completa
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {restante.map((c, i) => {
              const pos = i + 4;
              const color = AWARD_COLORS[c.award.cor];
              const time = TIMES.find(t => t.id === c.timeId);
              const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
              const pctDoLider = ranking[0].pontos > 0 ? (c.pontos / ranking[0].pontos) * 100 : 0;
              const eueu = c.id === meuId; // linha do corretor logado
              return (
                <div key={c.id} style={{
                  display: "grid",
                  gridTemplateColumns: "32px 40px 1fr auto auto",
                  alignItems: "center", gap: 14,
                  padding: eueu ? "14px 12px" : "12px 4px",
                  borderBottom: i < restante.length - 1 ? `1px solid ${T.border}` : "none",
                  background: eueu ? T.brandSoft : "transparent",
                  border: eueu ? `1px solid ${T.brand}44` : undefined,
                  borderRadius: eueu ? T.r.md : 0,
                  marginBottom: eueu ? 6 : 0,
                  marginTop: eueu ? 6 : 0,
                  position: "relative",
                }}>
                  {eueu && (
                    <div style={{
                      position: "absolute", left: -2, top: "50%", transform: "translateY(-50%)",
                      width: 3, height: "60%", background: T.brand, borderRadius: 2,
                    }} />
                  )}
                  <div className="serif" style={{
                    fontSize: eueu ? 20 : 18, color: eueu ? T.brand : T.textDim,
                    fontWeight: 600, textAlign: "center",
                  }}>
                    {pos}º
                  </div>
                  <Avatar initials={initials} size={eueu ? 38 : 34} tone={color.fg} photo={c.foto} />
                  <div style={{ minWidth: 0 }}>
                    {/* Linha 1: Nome + Pílula (colados) */}
                    <div style={{
                      fontSize: 13, fontWeight: eueu ? 600 : 500,
                      color: eueu ? T.brand : T.text, marginBottom: 3,
                      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                    }}>
                      {c.nome}
                      {eueu && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: 1,
                          color: T.brand, background: T.bg,
                          padding: "2px 7px", borderRadius: 100,
                          border: `1px solid ${T.brand}44`,
                          textTransform: "uppercase",
                        }}>VOCÊ</span>
                      )}
                      <PilulaAwardTime awardId={c.award.id} timeEmoji={time?.emoji} size="xs" />
                    </div>
                    {/* Linha 2: Nome do time */}
                    {time && (
                      <div style={{ fontSize: 10, color: T.textSub, marginBottom: 1 }}>
                        {time.nome}
                      </div>
                    )}
                    {/* Linha 3: Cidade */}
                    <div style={{ fontSize: 10, color: T.textDim, marginBottom: 4 }}>
                      📍 {c.cidade}-{c.uf}
                    </div>
                    {/* Barra de progresso relativa ao líder */}
                    <div style={{ width: "100%", height: 3, background: T.elev, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{
                        width: `${pctDoLider}%`, height: "100%",
                        background: color.fg, borderRadius: 2,
                      }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 60 }}>
                    <div className="serif" style={{
                      fontSize: eueu ? 22 : 20, fontWeight: 600,
                      color: eueu ? T.brand : color.fg, lineHeight: 1,
                    }}>
                      {c.pontos}
                    </div>
                    <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 0.5, textTransform: "uppercase" }}>pts</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Coluna do pódio (estilo BetUF — pedestal 3D) ────────────────────
function PodiumColumn({ corretor: c, posicao, time }) {
  const color = AWARD_COLORS[c.award.cor];
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const isFirst = posicao === 1;

  // Altura do pedestal: 1º > 2º > 3º
  const pedestalH = posicao === 1 ? 100 : posicao === 2 ? 70 : 50;
  const avatarSize = isFirst ? 88 : 68;

  // Cores dos pedestais (remetendo a medalhas)
  const pedestalColors = {
    1: { top: T.gold, bottom: "#b8852a", text: "#6d4f18" },
    2: { top: T.silver, bottom: "#7a7a7a", text: T.textSec },
    3: { top: T.bronze, bottom: "#8a5d37", text: "#6d4725" },
  };
  const pc = pedestalColors[posicao];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>

      {/* Coroa no #1 (inspiração img 2) */}
      {isFirst && (
        <div style={{ fontSize: 28, lineHeight: 1, marginBottom: -4, filter: "drop-shadow(0 2px 4px rgba(200,155,60,0.4))" }}>
          👑
        </div>
      )}

      {/* Avatar com halo (inspiração img 4) */}
      <div style={{ position: "relative" }}>
        <div style={{
          position: "relative",
          padding: 3,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${color.fg} 0%, ${color.border} 100%)`,
        }}>
          <Avatar initials={initials} size={avatarSize} tone={color.fg} photo={c.foto} />
        </div>
        {/* Medalha com número */}
        <div style={{
          position: "absolute", bottom: -6, right: -6,
          width: 28, height: 28, borderRadius: "50%",
          background: `linear-gradient(135deg, ${pc.top} 0%, ${pc.bottom} 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 14, fontWeight: 700,
          border: `2px solid ${T.bg}`,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}>{posicao}</div>
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.5 } 50% { opacity: 0.9 } }`}</style>

      {/* Nome */}
      <div style={{ textAlign: "center", marginTop: 4 }}>
        <div className="serif" style={{
          fontSize: isFirst ? 17 : 14, fontWeight: 600,
          color: T.brand, letterSpacing: -0.3, lineHeight: 1.2,
        }}>
          {c.nome.split(" ").slice(0, 2).join(" ")}
        </div>
      </div>

      {/* Award pílula + emoji do time (acima da cidade) */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginTop: 3 }}>
        <PilulaAwardTime awardId={c.award.id} timeEmoji={time?.emoji} size="sm" />
      </div>

      <div className="podio-loc" style={{ fontSize: 10, color: T.textSub, textAlign: "center", marginTop: 2 }}>
        {c.cidade}-{c.uf}
      </div>

      {/* Pontos */}
      <div className="serif" style={{
        fontSize: isFirst ? 32 : 24, fontWeight: 700,
        color: color.fg, letterSpacing: -0.8, lineHeight: 1,
      }}>
        {c.pontos}
        <span style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0, marginLeft: 4 }}>pts</span>
      </div>

      {/* Pedestal 3D */}
      <div style={{ width: "100%", marginTop: 8, position: "relative" }}>
        <div style={{
          height: pedestalH,
          background: `linear-gradient(180deg, ${pc.top} 0%, ${pc.bottom} 100%)`,
          borderTopLeftRadius: 8, borderTopRightRadius: 8,
          position: "relative",
          boxShadow: `
            inset 0 2px 4px rgba(255,255,255,0.3),
            inset 0 -2px 4px rgba(0,0,0,0.2),
            0 -2px 8px ${pc.top}40
          `,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div className="serif" style={{
            fontSize: isFirst ? 38 : 28, fontWeight: 700,
            color: pc.text, letterSpacing: -1,
            textShadow: "0 1px 2px rgba(255,255,255,0.3)",
          }}>
            {posicao}º
          </div>
        </div>
        {/* Base/Chão */}
        <div style={{
          height: 4, background: T.borderMid,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }} />
      </div>
    </div>
  );
}
